import { z } from 'zod';

const yougovMonthlyRowSchema = z.object({
  month: z.string(),
  region: z.string(),
  brand: z.string(),
  metric: z.string(),
  score: z.number().nullable(),
});

const yougovInsightsSchema = z.object({
  tldr: z.object({ bullet: z.array(z.string()) }),
  paragraph: z.string(),
});

const yougovDataSchema = z.object({
  brandName: z.string(),
  sourceFilename: z.string(),
  rowCount: z.number(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
  dateRange: z.object({ start: z.string().nullable(), end: z.string().nullable() }),
  availableRegions: z.array(z.string()),
  targetBrand: z.string(),
  availableBrands: z.array(z.string()),
  monthly: z.array(yougovMonthlyRowSchema),
  insights: yougovInsightsSchema.nullable(),
});

export type YougovMonthlyRow = z.infer<typeof yougovMonthlyRowSchema>;
export type YougovData = z.infer<typeof yougovDataSchema>;

export type YougovParseResult =
  | { status: 'absent' }
  | { status: 'invalid' }
  | { status: 'ok'; data: YougovData };

/**
 * `yougov_data` is an unconstrained jsonb column, so a blob written before a
 * shape change can still violate the current contract at runtime. Parsing here
 * keeps a bad upload from throwing deep inside aggregation and white-screening
 * the whole analysis page.
 */
export function parseYougovData(raw: unknown): YougovParseResult {
  if (raw === null || raw === undefined) return { status: 'absent' };
  const parsed = yougovDataSchema.safeParse(raw);
  return parsed.success ? { status: 'ok', data: parsed.data } : { status: 'invalid' };
}
